'use client';

import { useState, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '@/components/ui/AppIcon';
import MobileUnifiedProcess from './MobileUnifiedProcess';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const steps = {
    dev: [
        { number: '01', title: 'Discovery', subtitle: 'Understanding Goals', desc: 'In-depth consultation to map out business goals, target audience, and technical feasibility.', icon: 'MagnifyingGlassIcon' },
        { number: '02', title: 'Strategy', subtitle: 'Blueprint & Planning', desc: 'Comprehensive roadmap comprising timelines, wireframes, user flows, and architecture.', icon: 'MapIcon' },
        { number: '03', title: 'Design', subtitle: 'Visual Excellence', desc: 'Crafting pixel-perfect, intuitive interfaces that align perfectly with your brand identity.', icon: 'PaintBrushIcon' },
        { number: '04', title: 'Development', subtitle: 'Robust Engineering', desc: 'Turning designs into high-performance code using modern frameworks and best practices.', icon: 'CommandLineIcon' },
        { number: '05', title: 'Testing', subtitle: 'Quality Assurance', desc: 'Rigorous testing across all devices and scenarios to ensure a flawless user experience.', icon: 'ShieldCheckIcon' },
        { number: '06', title: 'Launch', subtitle: 'Go Live', desc: 'Seamless deployment with zero downtime, including SSL setup and team training.', icon: 'RocketLaunchIcon' }
    ],
    seo: [
        { number: '01', title: 'Audit', subtitle: 'Digital Health Check', desc: 'Complete technical SEO audit, backlink analysis, and competitor benchmarking.', icon: 'ChartBarIcon' },
        { number: '02', title: 'Keywords', subtitle: 'Search Strategy', desc: 'Identifying high-intent keywords and content gaps to drive qualified organic traffic.', icon: 'MagnifyingGlassCircleIcon' },
        { number: '03', title: 'Optimization', subtitle: 'On-Page SEO', desc: 'Fine-tuning meta tags, headers, and structure for maximum search engine visibility.', icon: 'AdjustmentsHorizontalIcon' },
        { number: '04', title: 'Local SEO', subtitle: 'GMB Dominance', desc: 'Optimizing Google Business Profile availability to capture local market share.', icon: 'MapPinIcon' },
        { number: '05', title: 'Content', subtitle: 'Authority Building', desc: 'Creating high-value content that establishes your brand as an industry thought leader.', icon: 'DocumentTextIcon' },
        { number: '06', title: 'Growth', subtitle: 'Analysis & Scale', desc: 'Monthly performance reporting with actionable insights for continuous improvement.', icon: 'PresentationChartLineIcon' }
    ]
};

const FloatingGeoms = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[10%] left-[5%] w-24 h-24 border-2 border-indigo-400/40 rounded-3xl rotate-12 bg-indigo-500/5 geom-float" />
        <div className="absolute top-[15%] right-[8%] w-32 h-32 border-2 border-violet-400/40 rounded-full bg-violet-500/5 geom-float" />
        <div className="absolute top-[40%] left-[8%] w-16 h-16 border-2 border-blue-400/40 rotate-45 bg-blue-500/5 geom-float" />
        <div className="absolute top-[50%] right-[15%] w-24 h-12 border-2 border-emerald-400/40 -rotate-12 rounded-full bg-emerald-500/5 geom-float" />
        <div className="absolute top-[70%] left-[12%] w-20 h-20 border-2 border-amber-400/40 rotate-12 rounded-tr-none rounded-3xl bg-amber-500/5 geom-float" />
        <div className="absolute bottom-[10%] right-[5%] w-40 h-40 border-2 border-pink-400/40 rounded-full bg-pink-500/5 geom-float" />
        <div className="absolute top-[30%] left-[50%] w-12 h-12 border-2 border-cyan-400/40 rotate-45 rounded-xl bg-cyan-500/5 geom-float" />
        <div className="absolute bottom-[20%] left-[2%] w-32 h-16 border-2 border-purple-400/40 rotate-90 rounded-full bg-purple-500/5 geom-float" />
    </div>
);

const UnifiedProcess = () => {
    const [activeTab, setActiveTab] = useState<'dev' | 'seo'>('dev');
    const containerRef = useRef<HTMLElement>(null);
    const lineRef = useRef<HTMLDivElement>(null);

    // Animate Burst
    const triggerBurst = (index: number, e: React.MouseEvent) => {
        const stepElement = (e.currentTarget as HTMLElement).closest('.process-step');
        if (!stepElement) return;

        // Use scoped selectors to find elements ONLY within this clicked step
        const particles = stepElement.querySelectorAll('.burst-particle');
        const ring = stepElement.querySelector('.burst-ring');
        const orb = stepElement.querySelector('.orb-core');
        const card = stepElement.querySelector('.step-card');

        // Reset
        gsap.set(particles, { x: 0, y: 0, opacity: 1, scale: 1 });
        gsap.set(ring, { scale: 0.5, opacity: 1, borderWidth: '2px' });

        // Explode Particles
        particles.forEach((p, i) => {
            const angle = (i / particles.length) * Math.PI * 2;
            const velocity = 80 + Math.random() * 60;
            gsap.to(p, {
                x: Math.cos(angle) * velocity,
                y: Math.sin(angle) * velocity,
                opacity: 0,
                duration: 0.8 + Math.random() * 0.4,
                ease: 'power2.out'
            });
        });

        // Expand Ring
        gsap.fromTo(ring,
            { scale: 0.5, opacity: 1 },
            { scale: 2.5, opacity: 0, duration: 0.8, ease: 'power2.out' }
        );

        // Pulse Orb
        gsap.fromTo(orb,
            { scale: 0.8 },
            { scale: 1, duration: 0.4, ease: 'elastic.out(1.5, 0.3)' }
        );

        // Pop Card
        gsap.fromTo(card,
            { scale: 0.98, y: 0 },
            { scale: 1, y: -5, duration: 0.3, ease: 'back.out(2)' }
        );
    };

    useGSAP(() => {
        const items = gsap.utils.toArray<HTMLElement>('.process-step');

        ScrollTrigger.getAll().forEach(t => {
            if (t.vars.trigger && (t.vars.trigger as HTMLElement).classList?.contains('process-step')) {
                t.kill();
            }
        });

        // Line Animation
        if (lineRef.current && containerRef.current) {
            gsap.fromTo(lineRef.current,
                { height: '0%' },
                {
                    height: '100%',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: 'top 40%',
                        end: 'bottom 80%',
                        scrub: 1
                    }
                }
            );
        }

        // Float Geoms
        gsap.to('.geom-float', {
            y: (i) => (i + 1) * -50,
            rotation: (i) => (i + 1) * 45,
            ease: 'none',
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1
            }
        });

        // Step Animations
        items.forEach((item, i) => {
            const xOffset = i % 2 === 0 ? -100 : 100;
            gsap.fromTo(item,
                { opacity: 0, x: xOffset, rotateY: i % 2 === 0 ? -15 : 15 },
                {
                    opacity: 1,
                    x: 0,
                    rotateY: 0,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: item,
                        start: 'top 90%',
                        end: 'top 60%',
                        scrub: 1
                    }
                }
            );
        });

    }, { scope: containerRef, dependencies: [activeTab] });



    // ... (existing code)

    return (
        <>
            {/* Desktop View */}
            <section ref={containerRef} className="hidden lg:block py-32 relative overflow-hidden bg-white">
                <FloatingGeoms />

                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-200 to-transparent" />
                <div className="absolute -left-64 top-1/3 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -right-64 bottom-1/3 w-[500px] h-[500px] bg-violet-100/50 rounded-full blur-3xl pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-24">
                        <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
                            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">Process</span>
                        </h2>

                        <div className="inline-flex p-1.5 rounded-full bg-slate-100 border border-slate-200 shadow-inner overflow-hidden relative">
                            <div className={`absolute top-1.5 bottom-1.5 rounded-full bg-white shadow-md transition-all duration-300 ease-out z-0 border border-slate-100/50 ${activeTab === 'dev' ? 'left-1.5 w-[140px]' : 'left-[148px] w-[140px]'}`} />
                            <button onClick={() => setActiveTab('dev')} className={`relative z-10 w-[140px] py-3 rounded-full text-sm font-bold transition-colors duration-300 ${activeTab === 'dev' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}>Development</button>
                            <button onClick={() => setActiveTab('seo')} className={`relative z-10 w-[140px] py-3 rounded-full text-sm font-bold transition-colors duration-300 ${activeTab === 'seo' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}>SEO & Branding</button>
                        </div>
                    </div>

                    <div className="max-w-5xl mx-auto relative">
                        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-slate-200 transform md:-translate-x-1/2">
                            <div ref={lineRef} className="relative w-full bg-gradient-to-b from-indigo-500 via-purple-500 to-blue-500 shadow-[0_0_10px_rgba(99,102,241,0.5)] origin-top h-0">
                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-8 h-8 pointer-events-none perspective-1000 z-50">
                                    <div className="relative w-full h-full preserve-3d animate-[spin_4s_linear_infinite]">
                                        <div className="absolute inset-0 bg-indigo-500/80 border border-white/50 translate-z-4" />
                                        <div className="absolute inset-0 bg-indigo-500/80 border border-white/50 -translate-z-4" />
                                        <div className="absolute inset-0 bg-violet-500/80 border border-white/50 rotate-y-90 translate-z-4" />
                                        <div className="absolute inset-0 bg-violet-500/80 border border-white/50 -rotate-y-90 translate-z-4" />
                                        <div className="absolute inset-0 bg-blue-500/80 border border-white/50 rotate-x-90 translate-z-4" />
                                        <div className="absolute inset-0 bg-blue-500/80 border border-white/50 -rotate-x-90 translate-z-4" />
                                        <div className="absolute inset-0 bg-white/50 blur-md rounded-full scale-50 animate-pulse" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-16 lg:space-y-24">
                            {steps[activeTab].map((step, i) => (
                                <div
                                    key={i}
                                    className={`process-step relative flex flex-col md:flex-row items-center gap-8 md:gap-16 ${i % 2 === 0 ? 'md:flex-row-reverse' : ''}`}
                                    onClick={(e) => triggerBurst(i, e)}
                                >
                                    <div className="w-full md:w-1/2 group cursor-pointer">
                                        <div className={`step-card relative p-8 rounded-3xl bg-white border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${i % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>
                                            <div className={`absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-white opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl`} />
                                            <div className={`relative z-10 flex flex-col ${i % 2 === 0 ? 'items-start' : 'md:items-end'}`}>
                                                <div className="mb-4 p-3 bg-indigo-50 rounded-xl text-indigo-600 inline-block">
                                                    <Icon name={step.icon as any} className="w-6 h-6" />
                                                </div>
                                                <h3 className="text-2xl font-bold text-slate-900 mb-1">{step.title}</h3>
                                                <span className="text-xs font-bold text-indigo-500 uppercase tracking-wider mb-4 block">{step.subtitle}</span>
                                                <p className="text-slate-600 leading-relaxed">{step.desc}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="absolute left-4 md:left-1/2 -translate-x-1/2 z-20 cursor-pointer group">
                                        <div className="relative w-16 h-16 flex items-center justify-center">
                                            {[...Array(8)].map((_, pIdx) => (
                                                <div
                                                    key={pIdx}
                                                    className="burst-particle absolute w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-0 pointer-events-none"
                                                />
                                            ))}

                                            <div className="burst-ring absolute inset-0 rounded-full border-2 border-indigo-400 opacity-0 pointer-events-none" />
                                            <div className="absolute inset-0 rounded-full border-2 border-dashed border-indigo-300/50 animate-[spin_10s_linear_infinite] group-hover:border-indigo-400" />
                                            <div className="orb-core absolute inset-1 rounded-full bg-gradient-to-br from-white/80 to-indigo-50/50 backdrop-blur-sm border border-white/60 shadow-lg shadow-indigo-500/20 group-hover:shadow-indigo-500/40 transition-shadow" />
                                            <div className="relative z-10 text-indigo-600 transform transition-transform duration-500 group-hover:rotate-12">
                                                <Icon name={step.icon as any} className="w-6 h-6" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`hidden md:block absolute top-[2rem] h-px bg-gradient-to-r from-indigo-300 to-transparent w-16 z-0 ${i % 2 === 0 ? 'left-[50%] -translate-x-full origin-right' : 'left-[50%] origin-left'}`} />

                                    <div className="hidden md:block w-1/2 text-center md:text-left pl-8 pointer-events-none">
                                        <span className="text-[120px] leading-none font-black text-slate-50 select-none opacity-50 relative top-4">
                                            {step.number}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Mobile View */}
            <div className="block lg:hidden">
                <MobileUnifiedProcess />
            </div>
        </>
    );
};

export default UnifiedProcess;
